import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback; defaults to a generic recoverable-error panel. */
  fallback?: (reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary — new for B2B (not present in the B2C reference app;
 * flagged as a gap in the B2C technical reference and required by
 * constitution Principle XIV / spec FR-122). Catches render-time errors in
 * its subtree and shows a recoverable, user-appropriate message instead of
 * a blank white screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback(this.reset);

      return (
        <div className="flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-danger bg-danger-light px-6 py-10 text-center">
          <AlertTriangle className="mb-3 h-8 w-8 text-danger" aria-hidden="true" />
          <p className="text-sm font-medium text-danger-text">Something went wrong.</p>
          <p className="mt-1 text-sm text-danger">Please try again. If the problem persists, contact your administrator.</p>
          <Button variant="danger" size="sm" className="mt-4" onClick={this.reset}>
            Retry
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
